import React, { useRef, useState } from 'react';
import AircraftLiveStatus from './AircraftLiveStatus';
import MobileBookingDayAgenda from './MobileBookingDayAgenda';
import { MEDIA_QUERIES, useMediaQuery } from '../responsive';
import {
  formatLocalDate,
  formatLocalTime,
  getBookingEndDate,
  getBookingStartDate,
} from './bookingUtils';

const START_HOUR = 7;
const END_HOUR = 24;
const ROW_HEIGHT = 66;
const HEADER_HEIGHT = 82;
const AIRCRAFT_COL_WIDTH = 124;

export default function BookingDayAgenda({
  selectedDate,
  selectedDayBookings,
  aircraftList,
  usersMap,
  trainingOptions,
  openBookingModal,
  openBookingDetails,
  getAircraftColour,
  changeDayView,
}) {
  const isPhone = useMediaQuery(MEDIA_QUERIES.phone);

  if (isPhone) {
    return (
      <MobileBookingDayAgenda
        selectedDate={selectedDate}
        selectedDayBookings={selectedDayBookings}
        aircraftList={aircraftList}
        usersMap={usersMap}
        trainingOptions={trainingOptions}
        openBookingModal={openBookingModal}
        openBookingDetails={openBookingDetails}
        changeDayView={changeDayView}
      />
    );
  }

  return (
    <DesktopBookingDayAgenda
      selectedDate={selectedDate}
      selectedDayBookings={selectedDayBookings}
      aircraftList={aircraftList}
      usersMap={usersMap}
      trainingOptions={trainingOptions}
      openBookingModal={openBookingModal}
      openBookingDetails={openBookingDetails}
      getAircraftColour={getAircraftColour}
    />
  );
}

function DesktopBookingDayAgenda({
  selectedDate,
  selectedDayBookings,
  aircraftList,
  usersMap,
  trainingOptions,
  openBookingModal,
  openBookingDetails,
  getAircraftColour,
}) {
  const scrollRef = useRef(null);
  const hours = buildHours();

  const now = new Date();
  const todayLocalDate = formatLocalDate(now);
  const nowHour = now.getHours();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const isTodaySelected = selectedDate === todayLocalDate;

  const showNowLine =
    isTodaySelected &&
    nowMinutes >= START_HOUR * 60 &&
    nowMinutes <= END_HOUR * 60;

  const nowLineTop =
  ((nowMinutes - START_HOUR * 60) / 60) * ROW_HEIGHT;

  function scrollAircraft(direction) {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: direction * AIRCRAFT_COL_WIDTH * 3,
      behavior: 'smooth',
    });
  }

  return (
    <div className="booking-day-agenda--desktop" style={styles.board}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>OPERATIONS BOARD</div>
          <h3 style={styles.title}>{formatSelectedDate(selectedDate)}</h3>
        </div>

        <div style={styles.controls}>
          <button type="button" onClick={() => scrollAircraft(-1)} style={styles.navButton}>
            ◀
          </button>
          <span style={styles.helper}>Fleet</span>
          <button type="button" onClick={() => scrollAircraft(1)} style={styles.navButton}>
            ▶
          </button>
        </div>
      </div>

      {aircraftList.length === 0 ? (
        <div style={styles.empty}>No aircraft available.</div>
      ) : (
        <div style={styles.boardShell}>
          {showNowLine && (
            <div
              style={{
                ...styles.fullNowLine,
                top: HEADER_HEIGHT + nowLineTop,
              }}
            />
          )}

          <TimeRail hours={hours} />

          <div ref={scrollRef} style={styles.aircraftScroll}>
            <div
              style={{
                ...styles.aircraftGrid,
                gridTemplateColumns: `repeat(${aircraftList.length}, ${AIRCRAFT_COL_WIDTH}px)`,
              }}
            >
              {aircraftList.map((aircraft) => {
                const colour = getAircraftColour(aircraft, aircraftList);
                const aircraftBookings = selectedDayBookings.filter(
                  (booking) => String(booking.aircraft_id) === String(aircraft.id)
                );

                return (
                  <AircraftColumn
                    key={aircraft.id}
                    aircraft={aircraft}
                    colour={colour}
                    hours={hours}
                    selectedDate={selectedDate}
                    isTodaySelected={isTodaySelected}
                    nowHour={nowHour}
                    bookings={aircraftBookings}
                    usersMap={usersMap}
                    trainingOptions={trainingOptions}
                    openBookingModal={openBookingModal}
                    openBookingDetails={openBookingDetails}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimeRail({ hours }) {
  return (
    <div style={styles.timeRail}>
      <div style={styles.timeHeader}>Time</div>

      {hours.map((hour) => (
        <div key={hour.time} style={{ ...styles.timeCell, height: ROW_HEIGHT }}>
          {hour.time}
        </div>
      ))}
    </div>
  );
}

function AircraftColumn({
  aircraft,
  colour,
  hours,
  selectedDate,
  isTodaySelected,
  nowHour,
  bookings,
  usersMap,
  trainingOptions,
  openBookingModal,
  openBookingDetails,
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        ...styles.aircraftColumn,
        ...(hovered ? styles.aircraftColumnHover : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.aircraftHeader}>
        <span style={{ ...styles.headerColourBar, background: colour }} />

        <div style={styles.aircraftRegRow}>
          <span style={{ ...styles.aircraftDot, background: colour }} />
          <span>{aircraft.registration}</span>
        </div>

        <div style={styles.aircraftType}>
          {aircraft.icao_type || 'Aircraft'}
        </div>

        <div style={getAircraftStatusStyle(aircraft.status)}>
          {getAircraftStatusLabel(aircraft.status)}
        </div>
        <AircraftLiveStatus aircraft={aircraft} />
      </div>

      <div style={{ ...styles.aircraftTimeline, height: hours.length * ROW_HEIGHT }}>
        {hours.map((hour) => {
          const isCurrentHour =
            isTodaySelected && nowHour === hour.hour;

          return (
            <button
              key={`${aircraft.id}-${hour.time}`}
              type="button"
              onClick={() => openBookingModal(aircraft.id, selectedDate, null, hour.time)}
              style={{
                ...styles.freeSlot,
                top: hour.index * ROW_HEIGHT,
                height: ROW_HEIGHT,
                background: isCurrentHour
                  ? 'rgba(85,199,255,0.06)'
                  : 'transparent',
              }}
              title={`Book ${aircraft.registration} at ${hour.time}`}
            />
          );
        })}

        {bookings.map((booking) => {
          const start = getBookingStartDate(booking);
          const end = getBookingEndDate(booking);
          const block = getBookingBlockPosition(start, end, selectedDate);

          if (!block) return null;

          const pilot = usersMap[booking.user_id];
          const student = usersMap[booking.student_user_id];
          const instructor = usersMap[booking.instructor_user_id];
          const course = trainingOptions?.enrolments?.find(
            (enrolment) => String(enrolment.id) === String(booking.course_enrolment_id)
          )?.training_courses;

          return (
            <BookingBlock
              key={booking.id}
              booking={booking}
              aircraft={aircraft}
              colour={colour}
              block={block}
              pilot={pilot}
              studentName={student?.full_name || student?.email || 'Student'}
              instructorName={instructor?.full_name || instructor?.email || 'Instructor'}
              courseLabel={course ? [course.code, course.name].filter(Boolean).join(' · ') : ''}
              start={start}
              end={end}
              selectedDate={selectedDate}
              openBookingDetails={openBookingDetails}
            />
          );
        })}
      </div>
    </div>
  );
}

function BookingBlock({
  booking,
  aircraft,
  colour,
  block,
  pilot,
  studentName,
  instructorName,
  courseLabel,
  start,
  end,
  selectedDate,
  openBookingDetails,
}) {
  const [hovered, setHovered] = useState(false);
  const isTraining = booking.booking_type === 'training';
  const bookingTime = formatBookingBlockTime(start, end, selectedDate);

  return (
    <button
      type="button"
      onClick={() => openBookingDetails(booking)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={isTraining
        ? `Training Flight, Student: ${studentName}, Instructor: ${instructorName}${courseLabel ? `, Course: ${courseLabel}` : ''}, ${bookingTime}`
        : undefined}
      title={isTraining
        ? `Training Flight | Student: ${studentName} | Instructor: ${instructorName}${courseLabel ? ` | Course: ${courseLabel}` : ''} | ${bookingTime}`
        : undefined}
      style={{
        ...styles.bookingBlock,
        ...(isTraining ? styles.trainingBookingBlock : {}),
        ...(hovered ? styles.bookingBlockHover : {}),
        top: block.top,
        height: block.height,
        borderColor: colour,
        background: `linear-gradient(135deg, ${hexToRgba(
          colour,
          0.30
        )}, rgba(255,255,255,0.05))`,
        boxShadow: hovered
          ? `0 14px 30px ${hexToRgba(colour, 0.22)}`
          : `0 10px 24px ${hexToRgba(colour, 0.12)}`,
      }}
    >
      <div style={{ ...styles.bookingAircraft, ...(isTraining ? styles.trainingBookingAircraft : {}) }}>
        {aircraft.registration}
        {isTraining && <span style={styles.trainingMarker}>TRAINING</span>}
      </div>
      <div style={isTraining ? styles.trainingPrimaryParticipant : styles.bookingPilot}>
        {isTraining ? `Student: ${studentName}` : (pilot?.full_name || pilot?.email || 'Booked')}
      </div>
      {isTraining && <div style={styles.trainingParticipant}>Instructor: {instructorName}</div>}
      {isTraining && courseLabel && <div style={styles.trainingParticipant}>Course: {courseLabel}</div>}
      <div style={isTraining ? styles.trainingBookingTime : styles.bookingTime}>
        {bookingTime}
      </div>
    </button>
  );
}

function formatShortBookingDate(date) {
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
  });
}

function formatBookingBlockTime(start, end, selectedDate) {
  if (!start || !end) return 'Booked';

  const startDate = formatLocalDate(start);
  const endDate = formatLocalDate(end);

  if (startDate === endDate) {
    return `${formatLocalTime(start)}–${formatLocalTime(end)}`;
  }

  if (selectedDate === startDate) {
    return `From ${formatLocalTime(start)} • until ${formatShortBookingDate(
      end
    )} ${formatLocalTime(end)}`;
  }

  if (selectedDate === endDate) {
    return `Returns ${formatLocalTime(end)} • since ${formatShortBookingDate(
      start
    )}`;
  }

  return `Booked • ${formatShortBookingDate(
    start
  )}–${formatShortBookingDate(end)}`;
}

function buildHours() {
  return Array.from({ length: END_HOUR - START_HOUR }, (_, index) => {
    const hour = START_HOUR + index;

    return {
      index,
      hour,
      time: `${String(hour).padStart(2, '0')}:00`,
    };
  });
}

function makeLocalDateAtHour(selectedDate, hour) {
  const date = new Date(`${selectedDate}T00:00:00`);

  if (!Number.isFinite(date.getTime())) return null;

  date.setHours(hour, 0, 0, 0);
  return date;
}

function getBookingBlockPosition(start, end, selectedDate) {
  if (!start || !end) return null;

  const boardStart = makeLocalDateAtHour(selectedDate, START_HOUR);
  const boardEnd = makeLocalDateAtHour(selectedDate, END_HOUR);

  if (!boardStart || !boardEnd) return null;

  const clippedStart = start < boardStart ? boardStart : start;
  const clippedEnd = end > boardEnd ? boardEnd : end;

  if (clippedEnd <= boardStart || clippedStart >= boardEnd || clippedEnd <= clippedStart) {
    return null;
  }

  const top = ((clippedStart.getTime() - boardStart.getTime()) / 3600000) * ROW_HEIGHT;
  const height = Math.max(
    34,
    ((clippedEnd.getTime() - clippedStart.getTime()) / 3600000) * ROW_HEIGHT
  );

  return { top, height };
}

function formatSelectedDate(value) {
  const date = new Date(`${value}T12:00:00`);
  if (!Number.isFinite(date.getTime())) return 'Select a day';

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function hexToRgba(hex, alpha) {
  const safeHex = String(hex || '').replace('#', '');
  if (safeHex.length !== 6) return `rgba(85,199,255,${alpha})`;

  const red = parseInt(safeHex.slice(0, 2), 16);
  const green = parseInt(safeHex.slice(2, 4), 16);
  const blue = parseInt(safeHex.slice(4, 6), 16);

  return `rgba(${red},${green},${blue},${alpha})`;
}

function getAircraftStatusLabel(status) {
  const value = String(status || 'serviceable').toLowerCase();

  if (value === 'aog') return '🔴 AOG';
  if (value === 'defect') return '🔴 DEFECT';
  if (value === 'deferred') return '🟠 DEFERRED';

  return '🟢 SERVICEABLE';
}

function getAircraftStatusStyle(status) {
  const value = String(status || 'serviceable').toLowerCase();

  const base = {
    marginTop: 4,
    padding: '0.16rem 0.4rem',
    borderRadius: 999,
    fontSize: '0.58rem',
    fontWeight: 950,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    lineHeight: 1.2,
  };

  if (value === 'aog' || value === 'defect') {
    return {
      ...base,
      color: '#ffb3b3',
      background: 'rgba(255, 77, 77, 0.14)',
      border: '1px solid rgba(255, 77, 77, 0.28)',
    };
  }

  if (value === 'deferred') {
    return {
      ...base,
      color: '#ffd27a',
      background: 'rgba(255, 210, 122, 0.12)',
      border: '1px solid rgba(255, 210, 122, 0.26)',
    };
  }

  return {
    ...base,
    color: '#8ff4b6',
    background: 'rgba(143, 244, 182, 0.11)',
    border: '1px solid rgba(143, 244, 182, 0.24)',
  };
}

const styles = {
  board: {
    marginTop: '1rem',
    padding: '1rem',
    borderRadius: 18,
    background: 'rgba(10,22,36,0.48)',
    border: '1px solid rgba(255,255,255,0.08)',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.9rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    marginBottom: '0.9rem',
  },

  kicker: {
    color: '#55c7ff',
    fontSize: '0.72rem',
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: '0.25rem',
  },

  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: '1.08rem',
    fontWeight: 850,
  },

  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
  },

  helper: {
    color: '#9fb6d1',
    fontSize: '0.78rem',
    fontWeight: 700,
  },

  navButton: {
    border: '1px solid rgba(85,199,255,0.24)',
    background: 'rgba(85,199,255,0.08)',
    color: '#d7ebff',
    borderRadius: 999,
    width: 34,
    height: 34,
    fontWeight: 900,
    cursor: 'pointer',
  },

  empty: {
    color: '#9fb6d1',
    padding: '0.9rem',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.035)',
  },

  boardShell: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '72px 1fr',
    gap: 6,
    minWidth: 0,
  },

  fullNowLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 2,
    background: 'rgba(87, 213, 255, 0.95)',
    boxShadow: '0 0 12px rgba(87, 213, 255, 0.75)',
    zIndex: 20,
    pointerEvents: 'none',
  },

  timeRail: {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    background: 'rgba(10,22,36,0.82)',
    borderRadius: 12,
  },

  timeHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 11,
    height: HEADER_HEIGHT,
    boxSizing: 'border-box',
    color: '#8db7e6',
    fontSize: '0.7rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '0.7rem 0.45rem',
    display: 'flex',
    alignItems: 'center',
  },

  timeCell: {
    color: '#ffffff',
    fontSize: '0.82rem',
    fontWeight: 900,
    padding: '0.65rem 0.45rem',
    boxSizing: 'border-box',
    borderTop: '1px solid rgba(255,255,255,0.07)',
  },

  aircraftScroll: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 10,
    scrollbarGutter: 'stable',
  },

  aircraftGrid: {
    display: 'grid',
    gap: 6,
    width: 'max-content',
  },

  aircraftColumn: {
    position: 'relative',
    borderRadius: 14,
    transition: 'background 160ms ease, box-shadow 160ms ease',
  },

  aircraftColumnHover: {
    background: 'rgba(85,199,255,0.035)',
    boxShadow: 'inset 0 0 0 1px rgba(85,199,255,0.08)',
  },

  aircraftHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    height: HEADER_HEIGHT,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.18rem',
    padding: '0.45rem 0.35rem',
    borderRadius: 12,
    background: 'linear-gradient(180deg, rgba(34,42,63,0.96), rgba(24,30,46,0.98))',
    border: '1px solid rgba(120,160,210,0.14)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    color: '#d7ebff',
    fontWeight: 900,
    fontSize: '0.78rem',
    letterSpacing: '0.04em',
    overflow: 'hidden',
  },

  headerColourBar: {
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 999,
    opacity: 0.95,
  },

  aircraftRegRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.35rem',
    fontSize: '0.82rem',
    fontWeight: 900,
  },

  aircraftType: {
    color: '#87a8d2',
    fontSize: '0.60rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.9,
  },

  aircraftDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    boxShadow: '0 0 6px rgba(255,255,255,0.18)',
    flexShrink: 0,
  },

  aircraftTimeline: {
    position: 'relative',
    borderRadius: 14,
    overflow: 'hidden',
    background: `repeating-linear-gradient(
      to bottom,
      rgba(255,255,255,0.018) 0px,
      rgba(255,255,255,0.018) ${ROW_HEIGHT - 1}px,
      rgba(255,255,255,0.075) ${ROW_HEIGHT - 1}px,
      rgba(255,255,255,0.075) ${ROW_HEIGHT}px
    )`,
  },

  freeSlot: {
    position: 'absolute',
    left: 0,
    right: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    zIndex: 1,
  },

  bookingBlock: {
    position: 'absolute',
    left: 5,
    right: 5,
    border: '1px solid',
    borderRadius: 13,
    padding: '0.55rem',
    color: '#d7ebff',
    cursor: 'pointer',
    textAlign: 'left',
    overflow: 'hidden',
    zIndex: 2,
    transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
  },

  trainingBookingBlock: {
    padding: '0.18rem 0.28rem',
  },

  bookingBlockHover: {
    transform: 'translateY(-1px)',
    filter: 'brightness(1.08)',
  },

  bookingAircraft: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.2rem',
    minWidth: 0,
    color: '#ffffff',
    fontSize: '0.72rem',
    fontWeight: 950,
    letterSpacing: '0.05em',
  },

  trainingBookingAircraft: {
    fontSize: '0.62rem',
    lineHeight: 1.05,
  },

  bookingPilot: {
    color: '#d7ebff',
    fontSize: '0.72rem',
    fontWeight: 750,
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  trainingMarker: {
    flex: '0 0 auto',
    padding: '0.03rem 0.16rem',
    border: '1px solid rgba(182, 234, 255, 0.55)',
    borderRadius: 3,
    color: '#d7f4ff',
    fontSize: '0.52rem',
    lineHeight: 1.25,
  },

  trainingParticipant: {
    marginTop: 1,
    overflow: 'hidden',
    color: '#c4d8ea',
    fontSize: '0.57rem',
    lineHeight: 1.05,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  trainingPrimaryParticipant: {
    marginTop: 1,
    overflow: 'hidden',
    color: '#d7ebff',
    fontSize: '0.59rem',
    fontWeight: 750,
    lineHeight: 1.05,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  trainingBookingTime: {
    marginTop: 1,
    overflow: 'hidden',
    color: '#9fb6d1',
    fontSize: '0.58rem',
    fontWeight: 700,
    lineHeight: 1.05,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  bookingTime: {
    color: '#9fb6d1',
    fontSize: '0.66rem',
    fontWeight: 700,
    marginTop: 3,
  },
};